import {
  Button,
  InsightsDuplicatesSkeleton,
  SectionHeading,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  BulkEditTransactionsDialog,
  DeleteDuplicatePagesDialog,
  DeleteTransactionsDialog,
  DuplicateCompareWorkspace,
  DuplicatePagesOverview,
  EditTransactionDialog,
  useFetchTransactions,
  useInsights,
  useInsightsMutations,
  useLoadingTransactions,
  useProject,
  useTransactions,
} from '@/modules/projects'
import {
  hasInsightsResult,
  isInsightsProcessing,
} from '@/modules/projects/status'
import { DuplicatesList } from '../components'

// The duplicate-transactions half needs an insights pass. Until it runs, prompt to start it
// instead of showing an empty list (which would read as "scanned, found none").
function DuplicateTransactionsGate() {
  const { successToast, errorToast } = useToast()
  const project = useProject()
  const insights = useInsights()
  const { createInsights } = useInsightsMutations()

  const generating = insights ? isInsightsProcessing(insights) : false

  const handleGenerate = () => {
    if (!project) return
    createInsights(project.slug)
      .then(() => successToast('Insights generation started'))
      .catch((error) => errorToast('Failed to generate insights', error))
  }

  return (
    <div className="space-y-3">
      <SectionHeading
        title="Duplicate transactions"
        description="Transactions flagged as potential duplicates"
      />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white px-6 py-4">
        <p className="text-sm text-muted-foreground">
          {generating
            ? 'Scanning your transactions for duplicates…'
            : 'Run an insights pass to scan your transactions for duplicates.'}
        </p>
        <Button
          className="cursor-pointer"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate insights'}
        </Button>
      </div>
    </div>
  )
}

export function DuplicatesPage() {
  const insights = useInsights()
  const project = useProject()
  const transactions = useTransactions()
  const isLoading = useLoadingTransactions()

  useFetchTransactions(project?.slug ?? '')

  if (isLoading || !insights || !project) return <InsightsDuplicatesSkeleton />

  return (
    <div className="space-y-8">
      <BulkEditTransactionsDialog />
      <DeleteTransactionsDialog />
      <EditTransactionDialog />
      {/* Repeated page ranges and their PDF compare. No insights pass needed; render nothing when there are no repeats. */}
      <DeleteDuplicatePagesDialog />
      <DuplicateCompareWorkspace />

      <DuplicatePagesOverview />
      {hasInsightsResult(insights) ? (
        <DuplicatesList
          slug={project.slug}
          duplicates={insights.duplicates}
          transactions={transactions}
        />
      ) : (
        <DuplicateTransactionsGate />
      )}
    </div>
  )
}
